import { useMemo, useState } from 'react'
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Button, ToggleButtonGroup, ToggleButton, Divider, Paper, Chip, Stack, AppBar, Toolbar, IconButton, Tabs, Tab, Slider, FormControlLabel, Switch, Select, MenuItem } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import ImageIcon from '@mui/icons-material/Image'
import LayersIcon from '@mui/icons-material/Layers'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import HomeIcon from '@mui/icons-material/Home'
import SettingsIcon from '@mui/icons-material/Settings'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { THEME_PRESETS } from '../theme/presets'
import { useThemeContext } from '../contexts/ThemeContext'

export default function AppearanceMockPage() {
  const { t } = useTranslation()
  const muiTheme = useTheme()
  const appMode: 'light' | 'dark' = (muiTheme.palette?.mode as any) || 'light'
  const appDark = appMode === 'dark'
  const { mode, setThemeId, toggleMode } = useThemeContext()
  const [selected, setSelected] = useState('galatea-navy')
  const [moduleKey, setModuleKey] = useState<'galatea' | 'bpa'>('galatea')
  const [wmEnabled, setWmEnabled] = useState(true)
  const [wmOpacity, setWmOpacity] = useState(0.06)
  const [wmScale, setWmScale] = useState(1)
  const [wmPosition, setWmPosition] = useState<'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'>('center')
  const theme = useMemo(() => THEME_PRESETS.find(p => p.id === selected) || THEME_PRESETS[0], [selected])
  const bg = mode === 'light'
    ? (selected === 'galatea-navy' ? '#E7F0FF' : '#F8FAFC')
    : '#0F172A'
  const paper = mode === 'light' ? '#FFFFFF' : '#1E293B'
  const text = mode === 'light' ? '#0B1220' : '#F1F5F9'
  const text2 = mode === 'light' ? '#475569' : '#CBD5E1'
  const controlText = appDark ? '#E2E8F0' : '#0B1220'
  const controlBorder = appDark ? '#E2E8F0' : 'rgba(0,0,0,0.23)'

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Aparência • Mock</Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={2} alignItems="center">
            <Chip label={t(`themePresets.${theme.id}.name`, { defaultValue: theme.name })} sx={{ bgcolor: theme.primary, color: '#fff' }} />
            <Typography variant="body2" color="text.secondary">{t(`themePresets.${theme.id}.recommendation`, { defaultValue: theme.recommendation })}</Typography>
          </Stack>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, v) => { if (v && v !== mode) toggleMode() }}
            size="small"
          >
            <ToggleButton value="light" aria-label={t('theme.light')}><LightModeIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="dark" aria-label={t('theme.dark')}><DarkModeIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Temas</Typography>
            <Grid container spacing={1.5}>
              {THEME_PRESETS.map(p => (
                <Grid item xs={6} sm={4} md={6} key={p.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: selected === p.id ? p.primary : controlBorder,
                      bgcolor: appDark ? (selected === p.id ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)') : undefined,
                      transition: 'background-color 120ms, border-color 120ms',
                      '&:hover': {
                        borderColor: selected === p.id ? p.primary : (appDark ? '#F1F5F9' : 'rgba(0,0,0,0.4)'),
                        bgcolor: appDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                      },
                    }}
                  >
                    <CardActionArea onClick={() => { setSelected(p.id); setThemeId(p.id) }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: controlText }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: p.primary }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: selected === p.id ? 600 : 500 }}>
                            {t(`themePresets.${p.id}.name`, { defaultValue: p.name })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: appDark ? '#CBD5E1' : 'text.secondary' }}>
                            {t(`themePresets.${p.id}.recommendation`, { defaultValue: p.recommendation })}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
            <Box sx={{ bgcolor: bg, color: text, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <AppBar position="static" sx={{ bgcolor: theme.primary }}>
                  <Toolbar sx={{ minHeight: 56 }}>
                    <IconButton edge="start" sx={{ color: '#fff' }}>
                      <MenuIcon />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1, color: '#fff' }}>{moduleKey === 'galatea' ? 'Galatea' : 'BPA'}</Typography>
                    <IconButton sx={{ color: '#fff' }}><SearchIcon /></IconButton>
                    <IconButton sx={{ color: '#fff' }}><NotificationsIcon /></IconButton>
                    <IconButton sx={{ color: '#fff' }}><AccountCircleIcon /></IconButton>
                  </Toolbar>
                </AppBar>
                <Box sx={{ p: 2, bgcolor: paper, color: text }}>
                  <Tabs value={moduleKey} onChange={(_, v) => setModuleKey(v)} textColor="inherit" indicatorColor="secondary">
                    <Tab value="galatea" label="Galatea" />
                    <Tab value="bpa" label="BPA" />
                  </Tabs>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" gap={1.5} sx={{ mb: 2 }}>
                    <Button startIcon={<HomeIcon />} variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Início</Button>
                    <Button startIcon={<SettingsIcon />} variant="outlined">Configurações</Button>
                  </Stack>
                  <Paper sx={{ bgcolor: paper, color: text, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Card de Exemplo</Typography>
                    <Typography variant="body2" sx={{ color: text2, mb: 2, fontWeight: 500 }}>Texto secundário com contraste do modo {mode}.</Typography>
                    <Stack direction="row" gap={1.5}>
                      <Button variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Primário</Button>
                      <Button variant="outlined">Secundário</Button>
                    </Stack>
                  </Paper>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle1">Branding • Mock</Typography>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo (Header)</Typography>
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <ImageIcon sx={{ color: controlText }} />
                <Typography variant="body2" sx={{ color: controlText }}>Pré-visualização</Typography>
              </Stack>
              <Box
                sx={{
                  width: 180,
                  height: 48,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f1f5f9',
                  color: '#0B1220',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>LOGO</Typography>
              </Box>
            </Box>
            <Stack direction="row" gap={1.5} sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: controlText,
                  borderColor: controlBorder,
                  '&:hover': {
                    borderColor: appDark ? '#F1F5F9' : controlBorder,
                    backgroundColor: appDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                Upload
              </Button>
              <Button size="small" variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Aprovar</Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Watermark (Fundo)</Typography>
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 2
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                <LayersIcon sx={{ color: controlText }} />
                <Typography variant="body2" sx={{ color: controlText }}>Pré-visualização</Typography>
              </Stack>
              <Box
                sx={{
                  height: 180,
                  borderRadius: 1,
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: paper,
                  '&::after': wmEnabled ? {
                    content: '""',
                    position: 'absolute',
                    width: `${wmScale * 60}%`,
                    height: `${wmScale * 60}%`,
                    left: wmPosition.includes('left') ? 16 : wmPosition.includes('right') ? 'auto' : '50%',
                    right: wmPosition.includes('right') ? 16 : 'auto',
                    top: wmPosition.includes('top') ? 16 : wmPosition.includes('bottom') ? 'auto' : '50%',
                    bottom: wmPosition.includes('bottom') ? 16 : 'auto',
                    transform: wmPosition === 'center' ? 'translate(-50%, -50%)' : 'none',
                    backgroundImage: mode === 'dark'
                      ? 'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 8px)'
                      : 'repeating-linear-gradient(45deg, rgba(0,0,0,0.5) 0, rgba(0,0,0,0.5) 2px, transparent 2px, transparent 8px)',
                    opacity: wmOpacity,
                    borderRadius: 2
                  } : {}
                }}
              />
            </Box>
            <Stack direction="row" gap={1.5} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              <FormControlLabel control={<Switch checked={wmEnabled} onChange={(e) => setWmEnabled(e.target.checked)} />} label="Ativo" />
              <Box sx={{ width: 200, px: 1 }}>
                <Typography variant="caption">Opacidade</Typography>
                <Slider size="small" value={wmOpacity} min={0} max={0.3} step={0.01} onChange={(_, v) => setWmOpacity(v as number)} />
              </Box>
              <Box sx={{ width: 200, px: 1 }}>
                <Typography variant="caption">Escala</Typography>
                <Slider size="small" value={wmScale} min={0.5} max={1.5} step={0.05} onChange={(_, v) => setWmScale(v as number)} />
              </Box>
              <Box sx={{ minWidth: 160 }}>
                <Typography variant="caption">Posição</Typography>
                <Select
                  size="small"
                  fullWidth
                  value={wmPosition}
                  onChange={(e) => setWmPosition(e.target.value as any)}
                >
                  <MenuItem value="center">Centro</MenuItem>
                  <MenuItem value="top-left">Topo Esquerda</MenuItem>
                  <MenuItem value="top-right">Topo Direita</MenuItem>
                  <MenuItem value="bottom-left">Base Esquerda</MenuItem>
                  <MenuItem value="bottom-right">Base Direita</MenuItem>
                </Select>
              </Box>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: controlText,
                  borderColor: controlBorder,
                  '&:hover': {
                    borderColor: appDark ? '#F1F5F9' : controlBorder,
                    backgroundColor: appDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                  },
                }}
              >
                Upload
              </Button>
              <Button size="small" variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Aprovar</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}
